import { useState } from 'react';
import { User, Phone, Mail, Info } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { deliveryAddressText } from '../work/geo';

// Customer info card + profile popup (mirrors the customer row in
// OrderCompleteCardView and CustomerProfileDetailView).
export default function CustomerCard({ order, profile }) {
  const [showProfile, setShowProfile] = useState(false);
  const photo = profile?.profileImageURL || profile?.photoURL || '';
  const phone = order.customerPhone || profile?.phone || '';
  const address = deliveryAddressText(order);
  const fullName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    order.customerName ||
    'Customer';

  return (
    <>
      <div className="space-y-2 rounded-2xl bg-white p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowProfile(true)}
            className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-blue-50 ring-2 ring-blue-200"
            aria-label="Customer profile"
          >
            {photo ? (
              <img src={photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-blue-500">
                <User size={18} />
              </span>
            )}
          </button>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
            {order.customerName || 'Customer'}
          </span>
          {phone && (
            <a
              href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500 text-white shadow-sm active:scale-95"
              aria-label="Call customer"
            >
              <Phone size={16} />
            </a>
          )}
        </div>
        {address && (
          <p className="flex items-start gap-2 text-[11px] text-gray-500">
            <span className="mt-px">📍</span>
            <span className="line-clamp-2">{address}</span>
          </p>
        )}
      </div>

      <Sheet open={showProfile} onClose={() => setShowProfile(false)} title="Customer Profile">
        <div className="space-y-4 p-4">
          <div className="flex justify-center">
            <div className="h-24 w-24 overflow-hidden rounded-full bg-blue-50 ring-4 ring-blue-200">
              {photo ? (
                <img src={photo} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-blue-400">
                  <User size={40} />
                </span>
              )}
            </div>
          </div>

          <div className="divide-y divide-gray-100 rounded-2xl bg-white shadow-sm border border-gray-100">
            <p className="px-4 pt-3 text-[10px] font-bold uppercase tracking-wide text-gray-400">
              Customer Information
            </p>
            <div className="flex items-center gap-3 px-4 py-3">
              <User size={16} className="text-blue-500" />
              <span className="flex-1 text-xs text-gray-500">Name</span>
              <span className="text-xs font-semibold text-gray-900">{fullName}</span>
            </div>
            {profile?.email && (
              <div className="flex items-center gap-3 px-4 py-3">
                <Mail size={16} className="text-blue-500" />
                <span className="flex-1 text-xs text-gray-500">Email</span>
                <span className="max-w-[55%] truncate text-xs font-semibold text-gray-900">
                  {profile.email}
                </span>
              </div>
            )}
          </div>

          {phone && (
            <a
              href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
              className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm border border-gray-100 active:bg-gray-50"
            >
              <Phone size={16} className="text-blue-500" />
              <span className="flex-1 text-xs text-gray-500">Phone</span>
              <span className="text-xs font-bold text-blue-600">☎️ Tap to Call</span>
            </a>
          )}

          <p className="flex items-center gap-2 text-[11px] text-gray-400">
            <Info size={14} /> This is your customer for this delivery
          </p>
        </div>
      </Sheet>
    </>
  );
}
